import type { TabState } from './const';
import type { NotificationCategory } from '@/services/notification/type';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchNotifications,
  markAllAsRead as markAllAsReadApi,
  markAsRead as markAsReadApi,
} from '@/services/notification';
import { useNotificationStore } from '@/stores/notification';
import { PAGE_SIZE, POLL_INTERVAL } from './const';

const emptyTab = (): TabState => ({ items: [], page: 0, hasMore: true });

export function useNotification() {
  const [tabData, setTabData] = useState<Record<NotificationCategory, TabState>>({
    update: emptyTab(),
    for_you: emptyTab(),
  });
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<NotificationCategory>('update');
  const unreadCount = useNotificationStore(state => state.unreadCount);
  const updateUnread = useNotificationStore(state => state.updateUnread);
  const forYouUnread = useNotificationStore(state => state.forYouUnread);
  const loadUnreadCount = useNotificationStore(state => state.loadUnreadCount);
  const clearUnreadCounts = useNotificationStore(state => state.clearUnreadCounts);

  const pollRef = useRef<ReturnType<typeof setInterval>>(undefined);
  const pendingRef = useRef(0);
  const loadingMoreRef = useRef(false);
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;

  // 当前 tab 的数据
  const currentTab = tabData[activeTab];

  // 加载指定 tab 的某一页
  const loadTabPage = useCallback(async (tab: NotificationCategory, pageNum: number, append: boolean) => {
    pendingRef.current += 1;
    setLoading(true);
    try {
      const result = await fetchNotifications({ page: pageNum, pageSize: PAGE_SIZE, category: tab });
      const data = result?.data;
      if (data) {
        setTabData(prev => ({
          ...prev,
          [tab]: {
            items: append ? [...prev[tab].items, ...data.list] : data.list,
            page: pageNum,
            hasMore: data.pagination.hasNextPage,
          },
        }));
      }
    } catch {
      // 静默失败
    } finally {
      pendingRef.current -= 1;
      if (pendingRef.current === 0) {
        setLoading(false);
      }
    }
  }, []);

  // 面板打开：重置状态 + 并行预加载两个 tab 的第一页 + 未读数
  const onPanelOpen = useCallback(() => {
    setSelectedId(null);
    setActiveTab('update');
    loadTabPage('update', 1, false);
    loadTabPage('for_you', 1, false);
    loadUnreadCount();
  }, [loadTabPage, loadUnreadCount]);

  // 切换 tab（无网络请求，直接切数据）
  const switchTab = useCallback((tab: NotificationCategory) => {
    setActiveTab(tab);
    setSelectedId(null);
  }, []);

  // 滚动加载更多（仅当前 tab，用 ref 防止同一页重复请求）
  const loadMore = useCallback(() => {
    const tab = activeTabRef.current;
    const state = tabData[tab];
    if (loadingMoreRef.current || !state.hasMore) {
 return;
}
    loadingMoreRef.current = true;
    loadTabPage(tab, state.page + 1, true).finally(() => {
      loadingMoreRef.current = false;
    });
  }, [tabData, loadTabPage]);

  // 标记单条已读（已读通知跳过，乐观更新列表项状态，未读计数由服务端刷新）
  const markAsRead = useCallback(async (id: string) => {
    // 已读通知无需重复请求
    const existing = tabData.update.items.find(n => n.id === id)
      ?? tabData.for_you.items.find(n => n.id === id);
    if (existing?.isRead) {
 return;
}

    const tab = activeTabRef.current;

    // 乐观更新：立即将列表项标记为已读
    setTabData(prev => {
      const updated = { ...prev };
      for (const key of Object.keys(updated) as NotificationCategory[]) {
        updated[key] = {
          ...updated[key],
          items: updated[key].items.map(n => {
            if (n.id === id && !n.isRead) {
              return { ...n, isRead: true };
            }
            return n;
          }),
        };
      }
      return updated;
    });

    try {
      await markAsReadApi(id);
      // 从服务端刷新准确的分类未读数
      loadUnreadCount();
    } catch {
      loadTabPage(tab, 1, false);
      loadUnreadCount();
    }
  }, [tabData, loadTabPage, loadUnreadCount]);

  // 全部已读（乐观更新两个 tab）
  const markAllAsRead = useCallback(async () => {
    setTabData(prev => {
      const updated = { ...prev };
      for (const key of Object.keys(updated) as NotificationCategory[]) {
        updated[key] = {
          ...updated[key],
          items: updated[key].items.map(n => ({ ...n, isRead: true })),
        };
      }
      return updated;
    });
    clearUnreadCounts();

    try {
      await markAllAsReadApi();
    } catch {
      loadTabPage(activeTabRef.current, 1, false);
      loadUnreadCount();
    }
  }, [clearUnreadCounts, loadTabPage, loadUnreadCount]);

  // 选择通知进入详情
  const selectNotification = useCallback((id: string) => {
    setSelectedId(id);
    markAsRead(id);
  }, [markAsRead]);

  const goBack = useCallback(() => setSelectedId(null), []);

  // 初始化：拉取未读数 + 轮询
  useEffect(() => {
    loadUnreadCount();
    pollRef.current = setInterval(loadUnreadCount, POLL_INTERVAL);
    return () => {
      if (pollRef.current) {
 clearInterval(pollRef.current);
}
    };
  }, [loadUnreadCount]);

  const selectedNotification = selectedId
    ? currentTab.items.find(n => n.id === selectedId) ?? null
    : null;

  return {
    notifications: currentTab.items,
    unreadCount,
    updateUnread,
    forYouUnread,
    loading,
    hasMore: currentTab.hasMore,
    selectedId,
    selectedNotification,
    activeTab,
    switchTab,
    loadMore,
    markAllAsRead,
    selectNotification,
    goBack,
    onPanelOpen,
  };
}
